import { Platform } from '@angular/cdk/platform';
import { Component, computed, DestroyRef, inject, model, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { ChatOptInNudgeService } from '@app/+authenticated/+communications/+chat/services/chat-opt-in-nudge.service';
import { StreamChatService } from '@app/+authenticated/+communications/+chat/services/stream-chat.service';
import { isPayrollPlanLocked, isPayrollVisible } from '@app/+authenticated/+payroll/payroll.access';
import {
  getCommandPaletteAriaShortcut,
  getCommandPaletteShortcutKeys,
} from '@app/+authenticated/command-palette/command-palette-shortcut';
import { commandPaletteEvents } from '@app/+authenticated/command-palette/events/command-palette.events';
import { ComponentMapKey, SidePanelService } from '@app/+authenticated/shared/app-side-panel/side-panel.service';
import { accountModuleStore } from '@app/reducers/account-module/account-module.state';
import { getAccount, getAccountSubscription } from '@app/reducers/account/account-service.selectors';
import { selectPlanIsAtleastBasic } from '@app/reducers/account/account-subscription.selector';
import {
  selectAccountHasChatAddon,
  selectAccountHasHrPlus,
  selectAccountIsOnTrial,
} from '@app/reducers/account/account.selector';
import { selectPermissionCanAccessAccountSettings } from '@app/reducers/orm/permission/permission.selectors';
import { getPageParamsState } from '@app/reducers/page-params-state/page-params.service';
import { buildScheduleLink } from '@app/reducers/page-params-state/schedule-link';
import { buildTimesheetLink } from '@app/reducers/page-params-state/timesheet-link';
import { HrProUpsellDialogService } from '@app/services/hr-pro-upsell/hr-pro-upsell-dialog.service';
import { isHrProUpsellApplicable } from '@app/services/hr-pro-upsell/hr-pro-upsell.access';
import { LabsDeepLinkService } from '@app/services/labs-deep-link.service';
import { LockType } from '@app/services/plan-lock/plan-lock-dialog.model';
import { PlanLockDialogService } from '@app/services/plan-lock/plan-lock-dialog.service';
import { ReferralProgramService } from '@app/services/referral-program.service';
import { RefinerService } from '@app/services/refiner.services';
import { userPreferencesStore } from '@app/signal-store/user-preferences/user-preferences.store';
import { injectDispatch } from '@ngrx/signals/events';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { getPermissionState } from '@reducers/auth/permission.helper';
import { AppState } from '@reducers/interfaces';
import { getAuthenticatedUser } from '@reducers/orm/employee/employee.selectors';
import { mgcIconAnniversaryCuteFi } from '@sb/mgc-icons/mgcIcon-anniversary_cute_fi.icon';
import { mgcIconAnniversaryCuteRe } from '@sb/mgc-icons/mgcIcon-anniversary_cute_re.icon';
import { mgcIconCalendarCuteFi } from '@sb/mgc-icons/mgcIcon-calendar_cute_fi.icon';
import { mgcIconCalendarCuteRe } from '@sb/mgc-icons/mgcIcon-calendar_cute_re.icon';
import { mgcIconCalendarMonthCuteFi } from '@sb/mgc-icons/mgcIcon-calendar_month_cute_fi.icon';
import { mgcIconCalendarMonthCuteRe } from '@sb/mgc-icons/mgcIcon-calendar_month_cute_re.icon';
import { mgcIconChartVerticalCuteFi } from '@sb/mgc-icons/mgcIcon-chart_vertical_cute_fi.icon';
import { mgcIconChartVerticalCuteRe } from '@sb/mgc-icons/mgcIcon-chart_vertical_cute_re.icon';
import { mgcIconChat3CuteFi } from '@sb/mgc-icons/mgcIcon-chat_3_cute_fi.icon';
import { mgcIconChat3CuteRe } from '@sb/mgc-icons/mgcIcon-chat_3_cute_re.icon';
import { mgcIconComment2CuteFi } from '@sb/mgc-icons/mgcIcon-comment_2_cute_fi.icon';
import { mgcIconComment2CuteRe } from '@sb/mgc-icons/mgcIcon-comment_2_cute_re.icon';
import { mgcIconDocumentsCuteFi } from '@sb/mgc-icons/mgcIcon-documents_cute_fi.icon';
import { mgcIconDocumentsCuteRe } from '@sb/mgc-icons/mgcIcon-documents_cute_re.icon';
import { mgcIconExposureCuteFi } from '@sb/mgc-icons/mgcIcon-exposure_cute_fi.icon';
import { mgcIconExposureCuteRe } from '@sb/mgc-icons/mgcIcon-exposure_cute_re.icon';
import { mgcIconGiftCuteRe } from '@sb/mgc-icons/mgcIcon-gift_cute_re.icon';
import { mgcIconGroup3CuteFi } from '@sb/mgc-icons/mgcIcon-group_3_cute_fi.icon';
import { mgcIconGroup3CuteRe } from '@sb/mgc-icons/mgcIcon-group_3_cute_re.icon';
import { mgcIconHome7CuteFi } from '@sb/mgc-icons/mgcIcon-home_7_cute_fi.icon';
import { mgcIconHome7CuteRe } from '@sb/mgc-icons/mgcIcon-home_7_cute_re.icon';
import { mgcIconHorn2CuteRe } from '@sb/mgc-icons/mgcIcon-horn_2_cute_re.icon';
import { mgcIconNews2CuteFi } from '@sb/mgc-icons/mgcIcon-news_2_cute_fi.icon';
import { mgcIconNews2CuteRe } from '@sb/mgc-icons/mgcIcon-news_2_cute_re.icon';
import { mgcIconPaperCuteFi } from '@sb/mgc-icons/mgcIcon-paper_cute_fi.icon';
import { mgcIconPaperCuteRe } from '@sb/mgc-icons/mgcIcon-paper_cute_re.icon';
import { mgcIconPerformanceCuteFi } from '@sb/mgc-icons/mgcIcon-performance_cute_fi.icon';
import { mgcIconPerformanceCuteRe } from '@sb/mgc-icons/mgcIcon-performance_cute_re.icon';
import { mgcIconPigMoneyCuteFi } from '@sb/mgc-icons/mgcIcon-pig_money_cute_fi.icon';
import { mgcIconPigMoneyCuteRe } from '@sb/mgc-icons/mgcIcon-pig_money_cute_re.icon';
import { mgcIconProfileCuteFi } from '@sb/mgc-icons/mgcIcon-profile_cute_fi.icon';
import { mgcIconProfileCuteRe } from '@sb/mgc-icons/mgcIcon-profile_cute_re.icon';
import { mgcIconQuestionCuteFi } from '@sb/mgc-icons/mgcIcon-question_cute_fi.icon';
import { mgcIconQuestionCuteRe } from '@sb/mgc-icons/mgcIcon-question_cute_re.icon';
import { mgcIconRightRegular } from '@sb/mgc-icons/mgcIcon-right_regular.icon';
import { mgcIconRocketCuteFi } from '@sb/mgc-icons/mgcIcon-rocket_cute_fi.icon';
import { mgcIconRocketCuteRe } from '@sb/mgc-icons/mgcIcon-rocket_cute_re.icon';
import { mgcIconSearchCuteRe } from '@sb/mgc-icons/mgcIcon-search_cute_re.icon';
import { mgcIconSettings3CuteFi } from '@sb/mgc-icons/mgcIcon-settings_3_cute_fi.icon';
import { mgcIconSettings3CuteRe } from '@sb/mgc-icons/mgcIcon-settings_3_cute_re.icon';
import { mgcIconStopwatchCuteFi } from '@sb/mgc-icons/mgcIcon-stopwatch_cute_fi.icon';
import { mgcIconStopwatchCuteRe } from '@sb/mgc-icons/mgcIcon-stopwatch_cute_re.icon';
import { mgcIconUserAdd2CuteFi } from '@sb/mgc-icons/mgcIcon-user_add_2_cute_fi.icon';
import { mgcIconUserAdd2CuteRe } from '@sb/mgc-icons/mgcIcon-user_add_2_cute_re.icon';
import { mgcIconWallet4CuteFi } from '@sb/mgc-icons/mgcIcon-wallet_4_cute_fi.icon';
import { mgcIconWallet4CuteRe } from '@sb/mgc-icons/mgcIcon-wallet_4_cute_re.icon';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { hasPermission } from '@sb/shared/permission/util';
import type { PermissionCheck } from '@sb/shared/permission/util';
import { TooltipDirective } from '@sb/tooltip';
import { BadgeComponent, NotificationBadgeComponent } from '@sb/ui/badge';
import {
  DisclosureFlyoutGroupDirective,
  DisclosureFlyoutPanelComponent,
  DisclosureFlyoutTriggerDirective,
} from '@sb/ui/disclosure';
import { IconComponent, IconRegistry } from '@sb/ui/icon';
import { LogoComponent } from '@sb/ui/logo';
import { SidebarModule } from '@sb/ui/navigation';
import { ProgressBarComponent } from '@sb/ui/progress-bar';
import { OnboardingChecklistService } from '@shared/onboarding/onboarding-checklist.service';
import { PlanType } from '@shiftbase-com/ts-shared/models';
import { combineLatest, filter, map } from 'rxjs';

import { SBHeaderService } from '../sb-header/sb-header.service';
import { AppSideNavProfileComponent } from './app-side-nav-profile.component';
import { AppSideNavUpsellComponent } from './app-side-nav-upsell/app-side-nav-upsell.component';
import { NavItem, NavItemDef, NavVisibilityContext, RouterLinkCommands } from './app-side-nav.model';

/**
 * The Employees section and its Absence calendar entry are gated on different permissions, so a
 * user can hold one without the other. `departments: 'any'` mirrors what `PermissionGuard` applies
 * to `/employees/absence`, so the entry appears exactly when the route would admit the user.
 */
const viewAllUsersCheck: PermissionCheck = { permissions: ['View all users'], departments: 'any' };
const viewAbsenteeCheck: PermissionCheck = { permissions: ['View absentee'], departments: 'any' };

@Component({
  selector: 'app-side-nav',
  imports: [
    AppSideNavProfileComponent,
    RouterModule,
    TranslatePipe,
    SidebarModule,
    AppSideNavUpsellComponent,
    BadgeComponent,
    NotificationBadgeComponent,
    IconComponent,
    DisclosureFlyoutGroupDirective,
    DisclosureFlyoutPanelComponent,
    DisclosureFlyoutTriggerDirective,
    ProgressBarComponent,
    LogoComponent,
    TooltipDirective,
  ],
  templateUrl: './app-side-nav.component.html',
})
export class AppSideNavComponent implements OnInit {
  myItemsNavId = 'nav-my-items';

  isMinimized = model(false);
  #store = inject<Store<AppState>>(Store);
  headerService = inject(SBHeaderService);
  #router = inject(Router);
  #destroyRef = inject(DestroyRef);
  #translate = inject(TranslateService);
  #featureService = inject(FeatureService);
  #refinerService = inject(RefinerService);
  #streamChatService = inject(StreamChatService);
  #chatOptInNudge = inject(ChatOptInNudgeService);
  #communicationsBadgeCount = computed(
    () => this.#streamChatService.totalUnreadCount() || (this.#chatOptInNudge.showOptInNudge() ? 1 : 0),
  );
  #referralProgramService = inject(ReferralProgramService);
  #sidePanelService = inject(SidePanelService);
  #planLockDialogService = inject(PlanLockDialogService);
  #hrProUpsellDialogService = inject(HrProUpsellDialogService);
  #commandPaletteDispatch = injectDispatch(commandPaletteEvents);
  #platform = inject(Platform);
  #onboardingChecklistService = inject(OnboardingChecklistService);

  authUser = this.#store.selectSignal(getAuthenticatedUser);

  showFeedbackSurvey = this.#featureService.isEnabledSignal(featureFlag.REFINER);
  shouldShowCelloLauncher = this.#referralProgramService.isVisible;

  commandPaletteEnabled = this.#featureService.isEnabledSignal(featureFlag.LABS_COMMAND_PALETTE);
  // Show the shortcut only where a keyboard-driven pointer exists (desktop, or an iPad with a
  // keyboard/trackpad). A pure OS check misfires on iPadOS 13+, which reports a desktop UA — so
  // Platform.IOS is false — yet has no hardware keyboard by default; the capability query covers
  // that case whether or not a keyboard is attached.
  #showCommandPaletteShortcut = this.#platform.isBrowser && matchMedia('(hover: hover) and (pointer: fine)').matches;
  #platformName = this.#platform.isBrowser ? navigator.platform : '';
  commandPaletteShortcut = this.#showCommandPaletteShortcut
    ? getCommandPaletteShortcutKeys(this.#platformName)
    : undefined;
  commandPaletteAriaShortcut = this.#showCommandPaletteShortcut
    ? getCommandPaletteAriaShortcut(this.#platformName)
    : null;

  #scheduleParams = toSignal(
    combineLatest([this.#store.select(getPageParamsState), this.#store.select(getAccountSubscription)]),
  );

  checklist = this.#onboardingChecklistService.checklist;
  checklistProgressLevel = computed(() => this.checklist()?.progress ?? 0);
  #scheduleLink = computed(() => {
    const params = this.#scheduleParams();
    if (!params) return '/schedule/employee/week';
    const [pageParams] = params;
    return buildScheduleLink(pageParams.schedule);
  });

  #listViewEnabled = toSignal(this.#featureService.isEnabled$(featureFlag.LABS_TIMESHEET_LIST_VIEW), {
    initialValue: false,
  });

  #timesheetLink = computed(() => {
    const params = this.#scheduleParams();
    if (!params) return '/timesheet/team/day';
    const [pageParams] = params;
    return buildTimesheetLink(pageParams.timesheet, this.#listViewEnabled());
  });

  #userPreferences = inject(userPreferencesStore);

  #permissionState = this.#store.selectSignal(getPermissionState);
  #subscription = this.#store.selectSignal(getAccountSubscription);
  #account = this.#store.selectSignal(getAccount);
  #planIsAtleastBasic = this.#store.selectSignal(selectPlanIsAtleastBasic);
  #chatOptedIn = this.#store.selectSignal(accountModuleStore.selectModuleEnabled('chatOptIn'));
  #hasHrPlus = this.#store.selectSignal(selectAccountHasHrPlus);
  #hasChatAddon = this.#store.selectSignal(selectAccountHasChatAddon);
  // `?? false`: the preference is undefined until the store has loaded, and an unknown master
  // switch must not surface a Labs entry point in the meantime.
  #labsEnabled = computed(() => this.#userPreferences.labs_enabled() ?? false);

  /**
   * Frozen like the labels below: `AuthenticatedGuard` only activates this component's host once
   * the permission state is in the store, and a user's own permissions do not change mid-session.
   */
  #canViewAllUsers = hasPermission(viewAllUsersCheck, this.#permissionState());

  isUnconfirmedOrFreePlan = computed(
    () => !this.#account()?.continue_subscription || this.#subscription()?.plan.type === PlanType.FREE,
  );

  hasManageAccountPermission = computed(() =>
    hasPermission({ permissions: ['Manage account'], userId: 'me', departments: 'any' }, this.#permissionState()),
  );

  #canAccessAccountSettings = this.#store.selectSignal(selectPermissionCanAccessAccountSettings);
  #isOnTrial = this.#store.selectSignal(selectAccountIsOnTrial);

  #hrProUpsellApplies = computed(() =>
    isHrProUpsellApplicable({
      featureEnabled: this.#featureService.isEnabled(featureFlag.TMP_HR_PRO_UPSELL),
      hasHrPro: this.#hasHrPlus(),
      canAccessSettings: this.#canAccessAccountSettings(),
      planIsAtleastBasic: this.#planIsAtleastBasic(),
      accountIsOnTrial: this.#isOnTrial() ?? false,
    }),
  );

  hasAppCenterPermission = computed(() =>
    hasPermission(
      {
        permissions: ['Manage API tokens', 'Manage payroll apps', 'Manage non payroll apps'],
        userId: 'me',
        departments: 'any',
      },
      this.#permissionState(),
    ),
  );

  #currentUrl = toSignal(
    this.#router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.#router.url },
  );

  #absenceOverviewNavItem: NavItemDef = {
    labelKey: this.#translate.instant('Absence calendar'),
    link: () => '/employees/absence',
    icon: 'calendar-month-cute-re',
    activeIcon: 'calendar-month-cute-fi',
    isAccessible: ({ permissions }) => hasPermission(viewAbsenteeCheck, permissions),
  };

  #employeeNavItem(): NavItemDef {
    if (this.#canViewAllUsers) {
      return {
        labelKey: this.#translate.instant('Employees'),
        link: () => '/employees',
        isAccessible: ({ permissions }) => hasPermission(viewAllUsersCheck, permissions),
        icon: 'group-3-cute-re',
        activeIcon: 'group-3-cute-fi',
        subItems: [
          {
            labelKey: this.#translate.instant('Employee'),
            link: () => '/employees',
            icon: 'group-3-cute-re',
            activeIcon: 'group-3-cute-fi',
          },
          this.#absenceOverviewNavItem,
          {
            labelKey: this.#translate.instant('Templates'),
            link: () => '/employees/templates/document',
            icon: 'paper-cute-re',
            activeIcon: 'paper-cute-fi',
            isAccessible: ({ permissions, isFeatureEnabled, planIsAtleastBasic }) =>
              planIsAtleastBasic &&
              isFeatureEnabled(featureFlag.TMP_CONTRACT_TEMPLATE) &&
              hasPermission({ permissions: ['Manage contract templates'] }, permissions),
          },
        ],
      };
    }
    return this.#absenceOverviewNavItem;
  }

  /**
   * `#navItemDefs` resolves its labels once, at construction, while the template's `translate`
   * pipe keeps re-evaluating. The language still changes after that — LanguageStartupService
   * resolves it reactively, and the user's own language outranks the one the first load used —
   * so the frozen labels and the pipe end up in different languages for the rest of the session.
   * Anything comparing against a nav label has to use this frozen copy rather than call
   * `instant()` again, or the two silently disagree.
   */
  #myItemsLabel = this.#translate.instant('My items');

  #navItemDefs: NavItemDef[] = [
    {
      labelKey: this.#translate.instant('Home'),
      link: () => '/dashboard',
      icon: 'home-7-cute-re',
      activeIcon: 'home-7-cute-fi',
    },
    {
      labelKey: this.#translate.instant('Communications'),
      link: () => '/communications',
      icon: 'comment-2-cute-re',
      activeIcon: 'comment-2-cute-fi',
      badgeCount: this.#communicationsBadgeCount,
      subItems: [
        {
          labelKey: this.#translate.instant('Chat'),
          link: () => '/communications/chat',
          icon: 'chat-3-cute-re',
          activeIcon: 'chat-3-cute-fi',
          isAccessible: ({
            isFeatureEnabled,
            planIsAtleastBasic,
            permissions,
            chatOptedIn,
            hasHrPlus,
            hasChatAddon,
          }) => {
            if (!planIsAtleastBasic || !isFeatureEnabled(featureFlag.TMP_STREAM_CHAT)) {
              return false;
            }
            if (isFeatureEnabled(featureFlag.TMP_CHAT_MODULE_OPT_IN)) {
              return true;
            }
            if (
              isFeatureEnabled(featureFlag.TMP_CHAT_MODULE_OPT_IN_ELIGIBILITY) &&
              (permissions.isAccountManager ?? false)
            ) {
              return true;
            }
            // Beta closed: a purchased Chat add-on grants access (mirrors backend hasChatAddon()).
            // Legacy grandfather (hr_plus + opt-in) kept until beta users are migrated.
            return hasChatAddon || (hasHrPlus && chatOptedIn);
          },
        },
        {
          labelKey: this.#translate.instant('News'),
          link: () => '/communications/news',
          icon: 'news-2-cute-re',
          activeIcon: 'news-2-cute-fi',
        },
        {
          labelKey: this.#translate.instant('Files'),
          link: () => '/communications/files',
          icon: 'documents-cute-re',
          activeIcon: 'documents-cute-fi',
          isAccessible: ({ planIsAtleastBasic }) => planIsAtleastBasic,
        },
      ],
    },
    {
      labelKey: this.#translate.instant('Schedule'),
      link: () => this.#scheduleLink(),
      icon: 'calendar-cute-re',
      activeIcon: 'calendar-cute-fi',
    },
    {
      labelKey: this.#translate.instant('Timesheet'),
      link: () => this.#timesheetLink(),
      isAccessible: ({ permissions }) =>
        hasPermission(
          { permissions: ['View all timesheets', 'View own timesheet'], userId: 'me', departments: 'any' },
          permissions,
        ),
      icon: 'stopwatch-cute-re',
      activeIcon: 'stopwatch-cute-fi',
    },
    {
      labelKey: this.#translate.instant('Payroll'),
      link: () => '/payroll',
      isAccessible: ({ isFeatureEnabled, labsEnabled, permissions }) =>
        isPayrollVisible({ labsEnabled, featureEnabled: isFeatureEnabled(featureFlag.LABS_PAYROLL_PREP) }, permissions),
      // An account below the flow's plan still sees the item and is upsold on click — see
      // `locked` on NavItemDef, and `isPayrollPlanLocked` for why plan is kept out of
      // `isAccessible`. That boundary lives in `payrollAccess`, not here.
      locked: () => isPayrollPlanLocked(this.#subscription()),
      onLockedClick: () => this.#planLockDialogService.openLockDialog(LockType.PAYROLL),
      icon: 'wallet-4-cute-re',
      activeIcon: 'wallet-4-cute-fi',
    },
    {
      labelKey: this.#translate.instant('Performance'),
      link: () => '/insights',
      isAccessible: ({ permissions }) => hasPermission({ permissions: ['View log'] }, permissions),
      icon: 'performance-cute-re',
      activeIcon: 'performance-cute-fi',
    },
    this.#employeeNavItem(),
    {
      labelKey: this.#translate.instant('Hiring'),
      link: () => '/hiring',
      isAccessible: ({ permissions, planIsAtleastBasic, isFeatureEnabled, hrProUpsellApplies }) => {
        if (!planIsAtleastBasic) return false;
        // A settings user without the add-on sees the item regardless of hiring permissions: it is
        // locked and upsells on click (see `locked` below).
        if (hrProUpsellApplies) return true;
        if (isFeatureEnabled(featureFlag.TMP_CONTRACT_TEMPLATE)) {
          return hasPermission({ permissions: ['Manage new hires'] }, permissions);
        }
        return isFeatureEnabled(featureFlag.TMP_HIRING_OPTIN) && (permissions.isAccountManager ?? false);
      },
      locked: () => this.#hrProUpsellApplies(),
      onLockedClick: () => this.#hrProUpsellDialogService.open(),
      icon: 'user-add-2-cute-re',
      activeIcon: 'user-add-2-cute-fi',
      subItems: [
        {
          labelKey: this.#translate.instant('New hire'),
          link: () => '/hiring/new-hires',
          icon: 'user-add-2-cute-re',
          activeIcon: 'user-add-2-cute-fi',
          isAccessible: ({ hasHrPlus }) => hasHrPlus,
        },
        {
          labelKey: this.#translate.instant('Templates'),
          link: () => '/hiring/templates/document',
          icon: 'paper-cute-re',
          activeIcon: 'paper-cute-fi',
          isAccessible: ({ hasHrPlus }) => hasHrPlus,
        },
      ],
    },
    {
      labelKey: this.#translate.instant('Reports'),
      link: () => '/reports',
      isAccessible: ({ permissions }) =>
        hasPermission({ permissions: ['View reports'], departments: 'any' }, permissions),
      icon: 'chart-vertical-cute-re',
      activeIcon: 'chart-vertical-cute-fi',
    },
    {
      id: this.myItemsNavId,
      labelKey: this.#myItemsLabel,
      link: () => '/my-account/profile',
      icon: 'profile-cute-re',
      activeIcon: 'profile-cute-fi',
      subItems: [
        {
          labelKey: this.#translate.instant('My profile'),
          link: () => '/my-account/profile',
          icon: 'profile-cute-re',
          activeIcon: 'profile-cute-fi',
        },
        {
          labelKey: this.#translate.instant('Schedule'),
          link: () => '/my-account/schedule',
          icon: 'calendar-cute-re',
          activeIcon: 'calendar-cute-fi',
        },
        {
          labelKey: this.#translate.instant('Timesheet'),
          link: () => '/my-account/timesheet',
          isAccessible: ({ permissions }) =>
            hasPermission(
              { permissions: ['View own timesheet', 'View all timesheets'], departments: 'any', userId: 'me' },
              permissions,
            ),
          icon: 'stopwatch-cute-re',
          activeIcon: 'stopwatch-cute-fi',
        },
        {
          labelKey: this.#translate.instant('Absence'),
          link: () => '/my-account/absence',
          isAccessible: ({ permissions, planIsAtleastBasic }) =>
            planIsAtleastBasic &&
            hasPermission(
              {
                permissions: ['View absentee', 'View own absentee', 'View vacation hours'],
                departments: 'any',
                userId: 'me',
              },
              permissions,
            ),
          icon: 'anniversary-cute-re',
          activeIcon: 'anniversary-cute-fi',
        },
        {
          labelKey: this.#translate.instant('Plus minus'),
          link: () => '/my-account/plus-minus',
          isAccessible: ({ permissions }) =>
            hasPermission(
              { permissions: ['View plus min hours', 'View own plus min hours'], departments: 'any', userId: 'me' },
              permissions,
            ),
          icon: 'exposure-cute-re',
          activeIcon: 'exposure-cute-fi',
        },
        {
          labelKey: this.#translate.instant('Minijob'),
          link: () => '/my-account/minijob',
          isAccessible: ({ permissions, planIsAtleastBasic }) =>
            planIsAtleastBasic &&
            !!this.authUser()?.minijob &&
            hasPermission(
              { permissions: ['View minijob', 'View own minijob'], departments: 'any', userId: 'me' },
              permissions,
            ),
          icon: 'pig-money-cute-re',
          activeIcon: 'pig-money-cute-fi',
        },
      ],
    },
  ];

  navItems = computed((): NavItem[] => {
    this.#currentUrl(); // reactive dependency — re-evaluates isActive on navigation
    const ctx: NavVisibilityContext = {
      permissions: this.#permissionState(),
      planIsAtleastBasic: this.#planIsAtleastBasic(),
      isFeatureEnabled: (flag: string) => this.#featureService.isEnabledSignal(flag)(),
      labsEnabled: this.#labsEnabled(),
      chatOptedIn: this.#chatOptedIn(),
      hasHrPlus: this.#hasHrPlus(),
      hasChatAddon: this.#hasChatAddon(),
      hrProUpsellApplies: this.#hrProUpsellApplies(),
    };

    return this.#navItemDefs
      .filter((def) => !def.isAccessible || def.isAccessible(ctx))
      .map((def) => {
        const link = def.link();
        const subItems = this.#mapSubItems(def.subItems, ctx);
        return {
          id: def.id,
          labelKey: def.labelKey,
          link,
          // The parent stays active when its own route or any of its sub-items is active.
          isActive: this.#isRouteActive(link, def.exact) || !!subItems?.some((subItem) => subItem.isActive),
          locked: def.locked?.(),
          onLockedClick: def.onLockedClick,
          icon: def.icon,
          activeIcon: def.activeIcon,
          beta: def.beta,
          badgeCount: def.badgeCount,
          subItems,
        };
      });
  });

  #mapSubItems(subDefs: NavItemDef[] | undefined, ctx: NavVisibilityContext): NavItem[] | undefined {
    const accessibleItems = subDefs?.filter(({ isAccessible }) => !isAccessible || isAccessible(ctx));

    const subItems = accessibleItems?.map(({ labelKey, icon, activeIcon, link, locked }) => {
      const subItemLink = link();
      return {
        labelKey,
        link: subItemLink,
        isActive: this.#isRouteActive(subItemLink, true),
        icon,
        activeIcon,
        locked: locked?.(),
      };
    });

    /**
     * Subitems are based on in page tabs. If only one tab is active, the parent link will navigate to that section,
     * making the subitem redundant.
     */
    return subItems?.length > 1 ? subItems : undefined;
  }

  constructor() {
    // Eager-instantiate so the `/universal/labs` deep link can open the dialog.
    inject(LabsDeepLinkService);

    inject(IconRegistry).registerIcons([
      mgcIconHome7CuteRe,
      mgcIconNews2CuteRe,
      mgcIconCalendarCuteRe,
      mgcIconCalendarMonthCuteRe,
      mgcIconStopwatchCuteRe,
      mgcIconPerformanceCuteRe,
      mgcIconGroup3CuteRe,
      mgcIconChartVerticalCuteRe,
      mgcIconQuestionCuteRe,
      mgcIconSettings3CuteRe,
      mgcIconHorn2CuteRe,
      mgcIconGiftCuteRe,
      mgcIconRocketCuteRe,
      mgcIconUserAdd2CuteRe,
      mgcIconDocumentsCuteRe,
      mgcIconChat3CuteRe,
      mgcIconRightRegular,
      mgcIconSearchCuteRe,
      mgcIconPaperCuteRe,
      mgcIconComment2CuteRe,
      mgcIconProfileCuteRe,
      mgcIconAnniversaryCuteRe,
      mgcIconExposureCuteRe,
      mgcIconPigMoneyCuteRe,
      mgcIconWallet4CuteRe,
      mgcIconHome7CuteFi,
      mgcIconComment2CuteFi,
      mgcIconChat3CuteFi,
      mgcIconNews2CuteFi,
      mgcIconDocumentsCuteFi,
      mgcIconCalendarCuteFi,
      mgcIconCalendarMonthCuteFi,
      mgcIconStopwatchCuteFi,
      mgcIconPerformanceCuteFi,
      mgcIconGroup3CuteFi,
      mgcIconPaperCuteFi,
      mgcIconUserAdd2CuteFi,
      mgcIconChartVerticalCuteFi,
      mgcIconProfileCuteFi,
      mgcIconAnniversaryCuteFi,
      mgcIconExposureCuteFi,
      mgcIconPigMoneyCuteFi,
      mgcIconWallet4CuteFi,
      mgcIconQuestionCuteFi,
      mgcIconSettings3CuteFi,
      mgcIconRocketCuteFi,
    ]);
  }

  ngOnInit(): void {
    this.headerService.supervisorRequestsSidebarOpen$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((open) => {
      if (open) {
        this.#openRequestsPanel('request');
        return;
      }
      this.#closeRequestsPanel();
    });

    this.headerService.myRequestsSidebarOpen$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((open) => {
      if (open) {
        this.#openRequestsPanel('my-request');
        return;
      }
      this.#closeRequestsPanel();
    });
  }

  #openRequestsPanel(requestedTab: 'request' | 'my-request') {
    this.#sidePanelService.setActiveComponent('requests', { tab: requestedTab });
  }

  // The streams also reset to false while another panel is open — so only close when
  // the requests panel is the one showing.
  #closeRequestsPanel(): void {
    if (!this.#sidePanelService.isActive('requests')) {
      return;
    }

    this.#sidePanelService.close();
  }

  openCommandPalette(): void {
    this.#commandPaletteDispatch.openRequested();
  }

  openFeedbackSurvey(): void {
    this.#refinerService.showSurvey('web-general-feedback');
  }

  handleReferralProgramClick(): void {
    this.#referralProgramService.handleClick('nav-sidebar');
  }

  openHelpCenter(): void {
    this.#sidePanelService.setActiveComponent('support');
  }

  isSidePanelActive(key: ComponentMapKey) {
    return this.#sidePanelService.isActive(key);
  }

  openChecklist() {
    this.#sidePanelService.setActiveComponent('checklist');
  }

  // Mirrors the locked-link pattern already used by `sb-nav-item` and the dashboard
  // sidebars: `[routerLink]="locked ? null : link"` disables navigation at the source
  // (RouterLink's own click handler no-ops once its urlTree is null — it doesn't check
  // `defaultPrevented`, so intercepting the click event alone can't stop it), and this
  // handler just runs the item's own locked action (an upgrade or upsell dialog). Enter-key
  // activation on the anchor fires the same `click`.
  handleLockedClick(item: NavItem): void {
    item.onLockedClick?.();
  }

  #isRouteActive(link: RouterLinkCommands, exact?: boolean): boolean {
    const urlTree = this.#router.createUrlTree(Array.isArray(link) ? link : [link]);
    return this.#router.isActive(urlTree, {
      paths: exact ? 'exact' : 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }
}
